import { cn } from "@win-reality/react-ui";
import { useState } from "react";

import { CONTENT_WIDTH } from "../../constants/layout";
import {
  getMetric,
  getMetricValue,
  MetricDefinition,
  MetricGroup,
} from "../../constants/metrics";
import { useMetricTiles } from "../../hooks/useMetricTiles";
import { Activity, Sport } from "../../types/gameFull";
import { ChangeMetricDialog } from "./ChangeMetricDialog";
import { MetricTile } from "./MetricTile";
import { MetricVisualizations } from "./MetricVisualizations";

type EditingTile = { group: MetricGroup; index: number };

/**
 * At-Bat Metrics: customizable metric tiles + pitch/hit visualizations,
 * below the Pitch Details header.
 */
export const PitchMetrics = ({
  activity,
  sport,
}: {
  activity: Activity;
  sport: Sport;
}) => {
  const { tiles, replaceTile, hideTile, resetTiles, isCustomized } =
    useMetricTiles();
  const [editingTile, setEditingTile] = useState<EditingTile | null>(null);

  const { pitchData, hitData } = activity.pitch;

  const renderTiles = (group: MetricGroup, label: string) => {
    const metrics = tiles[group]
      .map(getMetric)
      .filter((metric): metric is MetricDefinition => Boolean(metric));

    return (
      <section className="space-y-3 p-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-medium uppercase tracking-wide text-tagging-muted">
            {label}
          </h4>
          {isCustomized(group) && (
            <button
              type="button"
              data-testid="pitch-metrics-reset-tiles-button"
              data-metric-group={group}
              onClick={() => resetTiles(group)}
              className="text-xs font-semibold text-gray-900 underline hover:no-underline dark:text-white"
            >
              Reset
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {metrics.map((metric, index) => (
            <MetricTile
              key={`${metric.id}-${index}`}
              data-testid="pitch-metrics-change-metric-button"
              valueTestId="pitch-metrics-metric-value"
              data-metric={metric.id}
              label={metric.label}
              unit={metric.unit}
              value={getMetricValue(metric, pitchData, hitData)}
              onClick={() => setEditingTile({ group, index })}
            />
          ))}
        </div>
      </section>
    );
  };

  return (
    <div
      data-testid="pitch-metrics-panel"
      data-activity-id={activity.activityId}
      className="flex min-h-0 w-full flex-1 flex-col"
    >
      <div className="tagging-scrollbar flex-1 overflow-y-auto">
        <div className={cn(CONTENT_WIDTH, "space-y-4 px-3 py-6")}>
          {renderTiles("pitch", "Pitch Metrics")}

          <div className="p-3">
            <MetricVisualizations
              pitchData={pitchData}
              hitData={hitData}
              sport={sport}
            />
          </div>

          {renderTiles("hit", "Hit Metrics")}
        </div>
      </div>

      {editingTile && (
        <ChangeMetricDialog
          open
          group={editingTile.group}
          currentMetricId={tiles[editingTile.group][editingTile.index]}
          onClose={() => setEditingTile(null)}
          onSelect={(metricId) => {
            replaceTile(editingTile.group, editingTile.index, metricId);
            setEditingTile(null);
          }}
          onHide={() => {
            hideTile(editingTile.group, editingTile.index);
            setEditingTile(null);
          }}
        />
      )}
    </div>
  );
};
